import { DB_TYPE } from '@quesmed/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { dbConfig, EEnvironment } from '@/config';
import type { OmitFunctions } from '@/types';
import { ClientStorage, qsStorage } from '@/utils';
import { createSelectors } from './selector';

interface GlobalState {
  dbType: DB_TYPE;
  completedList: Record<string, boolean>;
  sideBar: boolean;
  sidebarCollapsed: boolean;
  environment: EEnvironment;

  reset: () => void;
  setDbType: (dbType: DB_TYPE) => void;
  setSideBar: (value: boolean) => void;
  setSidebarCollapsed: (value: boolean) => void;
  toggleSidebarCollapsed: () => void;
  filterOptions: <T = unknown>(options: T[] | undefined, dbType?: DB_TYPE) => T[];
  setCompletedList: (item: string, status: boolean, flush?: boolean) => void;
  setEnvironment: (environment: EEnvironment) => void;
}

const initialState: OmitFunctions<GlobalState> = {
  dbType: DB_TYPE.FINALS,
  sideBar: false,
  sidebarCollapsed: false,
  completedList: {},
  environment: EEnvironment.PRODUCTION,
};

export const globalState = create<GlobalState>()(
  persist(
    (set, get) => ({
      ...initialState,
      reset: () => {
        set(initialState);
      },
      setDbType: (dbType) => {
        set({ dbType });
        ClientStorage.session.setItem('ques-dB', dbType);
      },
      setSideBar: (sideBar) => {
        set({ sideBar });
      },
      setSidebarCollapsed: (sidebarCollapsed) => {
        set({ sidebarCollapsed });
      },
      toggleSidebarCollapsed: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },
      setEnvironment: (environment) => {
        set({ environment });
        ClientStorage.local.setItem('ques-envMode', environment);
      },
      setCompletedList: (item, status, flush) => {
        if (flush) {
          set({ completedList: {} });
          return;
        }
        const items = get().completedList;
        if (!items[item]) {
          items[item] = status;
          set({ completedList: items });
        }
      },
      filterOptions: (options) => {
        if (!options) {
          return [];
        }
        return options.filter((o) =>
          dbConfig[get().dbType ?? DB_TYPE.FINALS]?.names?.includes((o as any).label),
        );
      },
    }),
    {
      name: 'global-state',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        dbType: state.dbType,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    },
  ),
);

export const useGlobal = createSelectors(globalState);
